import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Home, Hospital, Users, ArrowRight, CheckCircle } from "lucide-react";

export default function HomePage() {
  const services = [
    {
      icon: Home,
      title: "Lodging Support",
      description: "We partner with local hotels to provide comfortable rooms for families visiting loved ones in the hospital."
    },
    {
      icon: Hospital,
      title: "Transportation",
      description: "Getting to and from the hospital shouldn't be a burden. We help coordinate transportation for families."
    },
    {
      icon: Users,
      title: "Daily Necessities",
      description: "From meals to toiletries, we ensure families have what they need to focus on what matters most."
    }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-right-top"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1765423587693-afd93c579a2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHx3YXJtJTIwY296eSUyMGhvdGVsJTIwcm9vbSUyMGludGVyaW9yJTIwc3VubGlnaHR8ZW58MHx8fHwxNzcwMzQ3NTY0fDA&ixlib=rb-4.1.0&q=85')`,
            filter: 'saturate(0.45) brightness(0.9)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/60 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-2xl space-y-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/90 text-sm">
              <Heart className="w-4 h-4 text-accent" />
              Supporting families when they need it most
            </div>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              A Place to Rest,<br />
              <span className="text-accent">Close to Care</span>
            </h1>
            
            <p className="text-lg text-white/80 leading-relaxed max-w-xl">
              When a loved one is in the hospital, the last thing families should worry about 
              is where to stay. Hope Hospitality connects families with free or reduced-cost 
              lodging, transportation, and daily necessities.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/about">
                <Button 
                  size="lg" 
                  className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground btn-hover h-14 px-8"
                  data-testid="hero-learn-more-button"
                >
                  Learn Our Story
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/donate">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full border-white/30 text-white hover:bg-white/10 h-14 px-8"
                  data-testid="hero-donate-button"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Support Our Mission
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-in-up">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              Comprehensive Support for Families
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              We understand the challenges families face when a loved one is hospitalized. 
              Our services are designed to ease that burden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 card-hover p-2"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                    <service.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-foreground">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Goals Stats */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground">Our Vision</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "500", label: "Families to Serve" },
              { number: "20", label: "Hotel Partners Goal" },
              { number: "15", label: "Hospital Partners Goal" },
              { number: "2,000", label: "Nights to Provide" }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="text-center"
                data-testid={`goal-stat-${index}`}
              >
                <div className="font-serif text-4xl md:text-5xl font-bold text-primary-foreground">
                  {stat.number}
                </div>
                <div className="text-primary-foreground/80 mt-2 text-sm md:text-base">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Hotel Partners CTA */}
            <Card className="bg-card border-border/50 overflow-hidden card-hover" data-testid="hotel-cta-card">
              <div 
                className="h-48 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url('https://images.unsplash.com/photo-1769123300291-81262063e667?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHx3YXJtJTIwY296eSUyMGhvdGVsJTIwcm9vbSUyMGludGVyaW9yJTIwc3VubGlnaHR8ZW58MHx8fHwxNzcwMzQ3NTY0fDA&ixlib=rb-4.1.0&q=85')`,
                  filter: 'saturate(0.45) brightness(0.9)'
                }}
              />
              <CardContent className="p-8 space-y-4">
                <h3 className="font-serif text-2xl font-bold text-foreground">
                  Hotels: Make an Impact
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Turn unused rooms into acts of compassion. Partner with us to support families 
                  and gain valuable community impact reporting for franchising purposes.
                </p>
                <ul className="space-y-2">
                  {["Community impact reporting", "Tax benefits", "Corporate social responsibility"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/hotel-partners">
                  <Button 
                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mt-4"
                    data-testid="hotel-partner-cta-button"
                  >
                    Become a Hotel Partner
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Hospital Partners CTA */}
            <Card className="bg-card border-border/50 overflow-hidden card-hover" data-testid="hospital-cta-card">
              <div 
                className="h-48 bg-cover bg-center"
                style={{ 
                  backgroundImage: `url('https://images.unsplash.com/photo-1717497932377-7758b8d5b45e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3NwaXRhbCUyMGV4dGVyaW9yJTIwZnJpZW5kbHklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzcwMzQ3NTY4fDA&ixlib=rb-4.1.0&q=85')` 
                }}
              />
              <CardContent className="p-8 space-y-4">
                <h3 className="font-serif text-2xl font-bold text-foreground">
                  Hospitals: Support Your Patients' Families
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Join our network of hospitals providing comprehensive care. Help your patients' 
                  families access lodging and support during their stay.
                </p>
                <ul className="space-y-2">
                  {["Enhanced patient experience", "Family support services", "Community partnership"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/hospital-partners">
                  <Button 
                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground mt-4"
                    data-testid="hospital-partner-cta-button"
                  >
                    Join as Hospital Partner
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Family Stories Section */}
      <section className="py-24 lg:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Who We Serve</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mt-3">
              Families Like These
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              These are the types of families Hope Hospitality is being built to help. 
              With your support, we can make these stories a reality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "When my mother was hospitalized 200 miles from home, I didn't know how I'd afford to stay nearby. Hope Hospitality gave me a comfortable place to rest so I could be there for her every day.",
                name: "Sarah M.",
                relation: "Daughter",
                location: "Dallas, TX",
                image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face"
              },
              {
                quote: "Our son was in the NICU for three weeks. Being able to stay close by instead of driving 4 hours each way made all the difference. We'll never forget this kindness.",
                name: "The Rodriguez Family",
                relation: "Parents",
                location: "Phoenix, AZ",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
              },
              {
                quote: "After my husband's accident, I was lost. Hope Hospitality not only gave me a place to stay but connected me with transportation to the hospital. They were angels.",
                name: "Margaret T.",
                relation: "Wife",
                location: "Denver, CO",
                image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face"
              }
            ].map((story, index) => (
              <Card 
                key={index} 
                className="bg-card border-border/50 card-hover"
                data-testid={`family-story-${index}`}
              >
                <CardContent className="p-8 space-y-6">
                  <div className="flex items-center gap-4">
                    <img 
                      src={story.image} 
                      alt={story.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-foreground">{story.name}</div>
                      <div className="text-sm text-muted-foreground">{story.relation} • {story.location}</div>
                    </div>
                  </div>
                  <blockquote className="text-muted-foreground leading-relaxed italic">
                    "{story.quote}"
                  </blockquote>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/stories">
              <Button 
                variant="outline" 
                className="rounded-full"
                data-testid="view-more-stories-button"
              >
                Read More Stories
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Donate CTA */}
      <section className="py-24 lg:py-32 bg-accent/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Heart className="w-16 h-16 text-accent mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
            Help Us Keep Families Together
          </h2>
          <p className="text-muted-foreground mt-4 text-lg leading-relaxed max-w-2xl mx-auto">
            Your generous donation helps provide lodging, transportation, and daily necessities 
            to families in need. Every dollar makes a difference.
          </p>
          <Link to="/donate">
            <Button 
              size="lg"
              className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground mt-8 h-14 px-10 btn-hover"
              data-testid="donate-cta-button"
            >
              <Heart className="w-5 h-5 mr-2" />
              Make a Donation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
